import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { InterestIcon } from './admin/AdminInterests';
import { formatDateRange } from '../utils/dates';
import ShareButton from './ShareButton';
import FeedbackForm from './FeedbackForm';
import PhotoLightbox from './PhotoLightbox';
import { trackRegistrationClick } from '../api/api';
import ActivityJoinSection from './ActivityJoinSection';
import { track } from '../utils/analytics';
import { stripHtml } from './SEOHead';
import { SafeHtml } from './SafeHtml';
import { getFormEmbedUrl } from '../utils/formEmbed';
import { VerifiedBadge, UnverifiedBadge } from './VerifiedBadge';

// Label style shared across Happening On, Happening At, Organised by
const LABEL_CLS = 'text-[10px] uppercase tracking-wide font-medium text-gray-400 mb-1';

// ── Slots left badge ─────────────────────────────────────────────────────────
function SlotsLeftBadge({ slotsLeft, className = '' }) {
  if (slotsLeft === null || slotsLeft === undefined) return null;
  const colorCls = slotsLeft >= 10
    ? 'text-green-700 bg-green-50 border-green-100'
    : slotsLeft >= 5
    ? 'text-yellow-700 bg-yellow-50 border-yellow-100'
    : 'text-amber-700 bg-amber-50 border-amber-100';
  return (
    <p className={`text-xs font-semibold px-2 py-0.5 rounded-full border inline-block ${colorCls} ${className}`}
       data-testid="slots-left-badge">
      {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} left
    </p>
  );
}

// Format "HH:MM" (24h) → "H:MM AM/PM"
const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const CARD_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatUpdateChipDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate()} ${CARD_MONTHS[d.getMonth()]}`;
  } catch { return ''; }
}

const ActivityCard = ({ activity, organizers, otherName, isPast, isInterested, onInterest, isAttended, onAttend, interests, isLoggedIn, feedbackStatus, alreadyRegistered, onRegistered, emailJustInterested = false, priority = false, shouldLoad = true, onVisible, sourceProps = {}, activeFilters = { dates: [], interests: [] }, showCardTutorial = false, onTutorialDismiss }) => {
  const navigate = useNavigate();
  const [attendedLocal, setAttendedLocal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [organizerDropdownOpen, setOrganizerDropdownOpen] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [headingOffTop, setHeadingOffTop] = useState(false);
  const [headingProgress, setHeadingProgress] = useState(0);
  const [emailAnimating, setEmailAnimating] = useState(false);
  const [coverLoaded, setCoverLoaded] = useState(priority);
  const [imgNaturals, setImgNaturals] = useState({}); // { idx: {w, h} }
  const [carouselHeight, setCarouselHeight] = useState(null); // null = use default aspect-ratio
  const carouselRef = useRef(null);
  const carouselBtnClickedRef = useRef(false);
  const cardRef = useRef(null);
  const headingSentinelRef = useRef(null);

  useEffect(() => { setAttendedLocal(isAttended || false); }, [isAttended]);

  // When flagged as just-interested via email, briefly show uninterested then flip with animation
  useEffect(() => {
    if (!emailJustInterested) return;
    setEmailAnimating(true);
    const t = setTimeout(() => {
      setEmailAnimating(false);
      toast.success("You're marked as Interested! See you there 🎉", { duration: 4000 });
    }, 500);
    return () => clearTimeout(t);
  }, [emailJustInterested]);

  const carouselPhotos = (() => {
    return (activity.media_urls || []).filter(Boolean);
  })();
  const isVideoUrl = (url) => /\.(mp4|mov|webm)(\?.*)?$/i.test(url || '');

  useEffect(() => {
    if (!carouselRef.current) return;
    const videos = carouselRef.current.querySelectorAll('video');
    videos.forEach((v, i) => {
      if (i === activePhotoIdx) { v.play().catch(() => {}); } else { v.pause(); }
    });
  }, [activePhotoIdx, expanded]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && carouselRef.current) {
          carouselRef.current.querySelectorAll('video').forEach((v) => v.pause());
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  // When card enters viewport (rootMargin pre-fires 300px early), notify parent to load this card's images
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !onVisible || shouldLoad) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible();
          obs.disconnect();
        }
      },
      { rootMargin: '300px', threshold: 0 }
    );
    obs.observe(card);
    return () => obs.disconnect();
  }, [shouldLoad, onVisible]);

  // If the first carousel item is a video, coverLoaded can't rely on img onLoad —
  // mark it true immediately once shouldLoad is active so remaining carousel items render.
  useEffect(() => {
    if (!shouldLoad || coverLoaded) return;
    if (carouselPhotos.length > 0 && isVideoUrl(carouselPhotos[0])) {
      setCoverLoaded(true);
    }
  }, [shouldLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  const nowMs = Date.now();
  const startTime = activity.start_time || '00:00';
  const endDateStr = activity.date_end || activity.date;
  const activityStartMs = activity.date ? new Date(`${activity.date}T${startTime}:00`).getTime() : null;
  const activityEndMs = endDateStr ? new Date(`${endDateStr}T${startTime}:00`).getTime() : null;

  const isEventFuture = activityStartMs != null && activityStartMs > nowMs;
  // For ongoing with no start date, treat as "currently happening" if no end date or end date in future
  const isOngoingNoDate = activity.is_ongoing && !activity.date;
  const isEventOngoing = isOngoingNoDate
    ? (!activity.date_end || new Date(`${activity.date_end}T00:00:00`).getTime() > nowMs)
    : (!isEventFuture && activityEndMs != null && nowMs < activityEndMs);
  const datePillClass = isEventFuture
    ? 'bg-green-100 text-green-800'
    : isEventOngoing
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-gray-100 text-gray-600';
  const statusDotClass = isEventFuture ? 'bg-green-500' : isEventOngoing ? 'bg-yellow-400' : 'bg-gray-400';

  const plainDesc = stripHtml(activity.description || '');
  const expandedText = [
    plainDesc,
    activity.things_to_bring ? `\n\nThings to Bring:\n${activity.things_to_bring}` : '',
    activity.skills_required ? `\n\nSkills Required:\n${activity.skills_required}` : '',
  ].join('').trim();

  const hasExpandableContent = carouselPhotos.length > 0 || !!expandedText;

  const handleCarouselScroll = (e) => {
    const newIdx = Math.round(e.currentTarget.scrollLeft / (e.currentTarget.clientWidth || 1));
    if (newIdx !== activePhotoIdx && !carouselBtnClickedRef.current) {
      track('activity_card_media_scroll', { ...cardContext, media_index_after: newIdx, media_count: carouselPhotos.length, direction: newIdx > activePhotoIdx ? 'next' : 'prev' });
    }
    setActivePhotoIdx(newIdx);
  };
  const scrollToPhoto = (idx) => {
    carouselBtnClickedRef.current = true;
    carouselRef.current?.scrollTo({ left: idx * (carouselRef.current.clientWidth || 0), behavior: 'smooth' });
    setActivePhotoIdx(idx);
    setTimeout(() => { carouselBtnClickedRef.current = false; }, 600);
  };
  const openLightbox = (photos, idx) => { setLightboxPhotos(photos); setLightboxIdx(idx); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    window.history.pushState({ karmanaLightbox: true }, '');
    const onPop = () => setLightboxOpen(false);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.karmanaLightbox) window.history.back();
    };
  }, [lightboxOpen]);

  const hasOrganizer = organizers.length > 0 || otherName;
  const address = activity.location?.address || activity.location?.locality;
  const isCardSticky = carouselPhotos.length > 0 || expanded;

  // Sentinel observer: heading becomes sticky only AFTER it scrolls behind the nav
  useEffect(() => {
    const sentinel = headingSentinelRef.current;
    if (!sentinel) return;
    const NAV_H = 90; // nav bar + small buffer
    const RANGE = 40; // pixels of scroll to complete font shrink

    // IntersectionObserver for the binary sticky state
    const obs = new IntersectionObserver(
      ([entry]) => {
        setHeadingOffTop(!entry.isIntersecting && entry.boundingClientRect.top < 100);
      },
      { threshold: 0, rootMargin: '-90px 0px 0px 0px' }
    );
    obs.observe(sentinel);

    // Scroll listener for proportional progress (runs at 60fps via rAF)
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!headingSentinelRef.current) return;
        const top = headingSentinelRef.current.getBoundingClientRect().top;
        if (top >= NAV_H) {
          setHeadingProgress(0);
        } else {
          setHeadingProgress(Math.min(1, (NAV_H - top) / RANGE));
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      obs.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Compute carousel height = max natural-image height (capped at default aspect-[4/5] height)
  useEffect(() => {
    const keys = Object.keys(imgNaturals);
    if (keys.length === 0) return;
    const containerW = carouselRef.current?.offsetWidth;
    if (!containerW) return;
    const maxDefaultH = Math.round(containerW * 5 / 4); // current default cap (aspect-[4/5])
    let maxH = 0;
    keys.forEach(k => {
      const { w, h } = imgNaturals[k];
      if (w > 0) maxH = Math.max(maxH, Math.min(Math.round(h * containerW / w), maxDefaultH));
    });
    if (maxH > 0) setCarouselHeight(maxH);
  }, [imgNaturals]);

  const cardContext = {
    activity_id: activity.id,
    activity_name: activity.name,
    activity_position: sourceProps.activity_position ?? null,
    activity_date: activity.date || null,
    activity_date_end: activity.date_end || null,
    activity_location_locality: activity.location?.locality || null,
    activity_interest_tags: activity.areas_of_interest || [],
    organisation_id: sourceProps.organisation_id || activity.changemaker_id || null,
    organisation_name: sourceProps.organisation_name || organizers[0]?.name || otherName || null,
    filter_active: (activeFilters?.dates?.length > 0 || activeFilters?.interests?.length > 0),
    active_filters: activeFilters,
    is_past: isPast,
    source_page: sourceProps.source_page || 'activities',
  };

  return (
    <div
      ref={cardRef}
      className={`bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)]`}
      style={{ overflow: 'clip' }}
      data-testid={`activity-card-${activity.id}`}
    >
      <div className="px-4 pt-4">

        {/* 1. Heading — sentinel marks natural position; heading sticks only after scrolling behind nav */}
        <div ref={headingSentinelRef} className="h-0 w-full" aria-hidden="true" />
        <h3
          className={`font-bold text-gray-900 leading-tight ${
            headingOffTop && isCardSticky
              ? 'sticky top-14 bg-white z-10 -mx-4 px-4 pt-2 pb-2 mb-1 shadow-[0_2px_6px_rgba(0,0,0,0.06)]'
              : 'mb-2.5'
          }`}
          style={{
            fontSize: `${24 - 6 * (isCardSticky ? headingProgress : 0)}px`,
          }}
          data-testid={`activity-title-${activity.id}`}
        >
          <a
            href={`/activities/${activity.slug || activity.id}`}
            onClick={(e) => { e.stopPropagation(); track('activity_card_click', { ...cardContext, click_target: 'heading' }); }}
            className="text-gray-900 hover:text-gray-900 no-underline"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {activity.name}
          </a>
        </h3>

        {/* Completed tag — shown for past activities, below heading */}
        {isPast && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 mb-2.5">
            Completed
          </span>
        )}

        {/* Slots left badge — shown only for capacity-limited activities */}
        {activity.is_participants_fixed && activity.slots_left != null && (
          <SlotsLeftBadge slotsLeft={activity.slots_left} className="mb-2.5" />
        )}

        {/* 2. Interest tags — just below heading */}
        {activity.areas_of_interest?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {activity.areas_of_interest.map((interestId) => {
              const interest = interests.find((i) => i.id === interestId);
              if (!interest) return null;
              return (
                <span key={interestId} className="px-2 py-0.5 bg-white text-gray-600 text-[11px] rounded-full flex items-center gap-1 border border-gray-300">
                  <InterestIcon icon={interest.icon} size={11} className="text-gray-500 flex-shrink-0" />
                  {interest.name}
                </span>
              );
            })}
          </div>
        )}

        {/* 3. Carousel — always shown */}
        {carouselPhotos.length > 0 && (
          <div className="-mx-4 mb-0 relative group">
            <div
              ref={carouselRef}
              className="flex overflow-x-auto snap-x snap-mandatory"
              style={{
                scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch',
                ...(carouselHeight ? { height: carouselHeight + 'px', transition: 'height 0.3s ease' } : {}),
              }}
              onScroll={handleCarouselScroll}
              data-testid={`card-carousel-${activity.id}`}
            >
              {carouselPhotos.map((url, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-full snap-center bg-gray-50 relative"
                  style={carouselHeight ? { height: carouselHeight + 'px' } : {}}
                >
                  {/* Placeholder: card not yet scheduled to load, or carousel waiting for first image */}
                  {!shouldLoad || (i > 0 && !coverLoaded) ? (
                    <div className="w-full aspect-[4/5] md:aspect-[16/9] bg-gray-100" />
                  ) : isVideoUrl(url) ? (
                    <div className={`relative w-full ${carouselHeight ? 'h-full' : 'aspect-[4/5] md:aspect-[16/9]'}`}>
                      <video
                        src={url}
                        className="w-full h-full object-contain cursor-pointer"
                        onClick={() => { track('activity_card_media_click', { ...cardContext, media_index: i, media_type: 'video' }); openLightbox(carouselPhotos, i); }}
                        preload="metadata"
                        playsInline
                        muted
                        loop
                        onLoadedMetadata={(e) => {
                          const { videoWidth: w, videoHeight: h } = e.target;
                          if (w > 0) setImgNaturals(prev => ({ ...prev, [i]: { w, h } }));
                        }}
                        data-testid={`card-photo-${activity.id}-${i}`}
                      />
                      {activePhotoIdx !== i && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-14 h-14 rounded-full bg-black/50 flex items-center justify-center">
                            <div className="w-0 h-0 border-t-[10px] border-b-[10px] border-l-[18px] border-transparent border-l-white ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={`${activity.name} photo ${i + 1}`}
                      className={`w-full cursor-pointer object-contain ${carouselHeight ? 'h-full' : 'aspect-[4/5] md:aspect-[16/9]'}`}
                      onClick={() => { track('activity_card_media_click', { ...cardContext, media_index: i, media_type: 'image' }); openLightbox(carouselPhotos, i); }}
                      onLoad={(e) => {
                        if (i === 0) setCoverLoaded(true);
                        const { naturalWidth: w, naturalHeight: h } = e.target;
                        if (w > 0) setImgNaturals(prev => ({ ...prev, [i]: { w, h } }));
                      }}
                      loading="eager"
                      fetchPriority={priority && i === 0 ? 'high' : 'auto'}
                      data-testid={`card-photo-${activity.id}-${i}`}
                    />
                  )}
                </div>
              ))}
            </div>
            {carouselPhotos.length > 1 && (
              <>
                <button
                  className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60 z-10"
                  onClick={(e) => { e.stopPropagation(); const ni = (activePhotoIdx - 1 + carouselPhotos.length) % carouselPhotos.length; track('activity_card_media_scroll', { ...cardContext, media_index_after: ni, media_count: carouselPhotos.length, direction: 'prev' }); scrollToPhoto(ni); }}
                  data-testid={`carousel-prev-${activity.id}`}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60 z-10"
                  onClick={(e) => { e.stopPropagation(); const ni = (activePhotoIdx + 1) % carouselPhotos.length; track('activity_card_media_scroll', { ...cardContext, media_index_after: ni, media_count: carouselPhotos.length, direction: 'next' }); scrollToPhoto(ni); }}
                  data-testid={`carousel-next-${activity.id}`}
                >
                  <ChevronRight size={18} />
                </button>
                <div className="flex justify-center items-center gap-1.5 py-2">
                  {carouselPhotos.map((_, i) => (
                    <button
                      key={i}
                      className={`rounded-full transition-all duration-200 ${i === activePhotoIdx ? 'w-4 h-1.5 bg-gray-700' : 'w-1.5 h-1.5 bg-gray-300'}`}
                      onClick={() => scrollToPhoto(i)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* 3. Description preview (2 lines) + Show More */}
        {expandedText && (
          <div className="px-0 pt-3 pb-1">
            <div className={expanded ? '' : 'line-clamp-2'}>
              <SafeHtml
                html={activity.description}
                className="text-gray-600 text-sm leading-relaxed prose prose-sm max-w-none"
              />
            </div>
            {/* Show More appears right under truncated text */}
            {!expanded && (
              <button
                onClick={() => {
                  track('activity_see_more_click', { ...cardContext });
                  setExpanded(true);
                }}
                className="text-green-600 text-sm mt-1.5 hover:text-green-700 transition-colors"
                data-testid={`about-expand-${activity.id}`}
              >
                Show More
              </button>
            )}
            {expanded && activity.things_to_bring && (
              <div className="mt-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Things to Bring</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{activity.things_to_bring}</p>
              </div>
            )}
            {expanded && activity.skills_required && (
              <div className="mt-2">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Skills Required</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{activity.skills_required}</p>
              </div>
            )}
            {/* Show Less appears at the bottom of all expanded content */}
            {expanded && (
              <button
                onClick={() => setExpanded(false)}
                className="text-green-600 text-sm mt-2 hover:text-green-700 transition-colors"
                data-testid={`about-expand-${activity.id}`}
              >
                Show Less
              </button>
            )}
          </div>
        )}

        {/* 4. Organised by — label on top, main org + collaborators in same row */}
        {hasOrganizer && (
          <div className="mb-2.5 relative mt-3">
            <p className={LABEL_CLS}>Organised by</p>

            <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
              {/* Main organiser */}
              {organizers.slice(0, 1).map((cm) => (
                <div
                  key={cm.id}
                  className="flex items-center gap-1.5 min-w-0 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); track('organisation_click', { organisation_id: cm.id, organisation_name: cm.name, source_component: 'activity_card', activity_position: sourceProps.activity_position ?? null, activity_id: activity.id }); }}
                >
                  <div className="w-5 h-5 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
                    {cm.logo_url ? (
                      <img src={cm.logo_url} alt={cm.name} className="w-full h-full object-contain bg-white" />
                    ) : (
                      <Users className="w-3 h-3 text-gray-400" />
                    )}
                  </div>
                  <span className="text-xs font-bold text-gray-500 truncate">{cm.name}</span>
                  {cm.is_claimed
                    ? <VerifiedBadge />
                    : <UnverifiedBadge orgId={cm.id} orgName={cm.name} />}
                </div>
              ))}

              {/* Collaborating orgs */}
              {(activity.collaborators || []).map((collab) => (
                <div key={collab.id} className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[10px] text-gray-400 leading-none uppercase tracking-wide">
                    {collab.relationship_type === 'other'
                      ? collab.relationship_custom_text
                      : { organiser: 'Organised By', co_organiser: 'Co-organised by', partner: 'In partnership with', sponsor: 'Sponsored by' }[collab.relationship_type] || 'Co-organised by'}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
                      {collab.org_logo_url
                        ? <img src={collab.org_logo_url} alt={collab.org_name} className="w-full h-full object-contain bg-white" />
                        : <Users className="w-3 h-3 text-gray-400" />}
                    </div>
                    <span className="text-xs font-bold text-gray-500 truncate">{collab.org_name}</span>
                    {collab.org_id && (collab.org_is_claimed
                      ? <VerifiedBadge />
                      : <UnverifiedBadge orgId={collab.org_id} orgName={collab.org_name} />)}
                  </div>
                </div>
              ))}

              {otherName && organizers.length === 0 && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-5 h-5 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                    <Users className="w-3 h-3 text-gray-400" />
                  </div>
                  <span className="text-xs font-bold text-gray-500 truncate">{otherName}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 5. Happening On + Happening At */}
        <div className="flex gap-3 mb-6 mt-3">

          {/* Happening On */}
          <div className="flex-shrink-0 w-[180px] flex flex-col">
            <p className={LABEL_CLS + ' flex items-center gap-1.5'}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDotClass}`} />
              <span>Happening on</span>
            </p>
            <div className="flex flex-col gap-1.5 pt-0.5">
              <div className="flex items-start gap-1.5">
                <Calendar size={13} className="flex-shrink-0 mt-[1px] text-gray-500" />
                {activity.is_ongoing ? (
                  /* Ongoing activities: show Last Date if available, else show "Ongoing" */
                  <span className="text-[13px] font-bold leading-snug text-gray-900">
                    {activity.date_end
                      ? `Last Date: ${formatDateRange(activity.date_end, null)}`
                      : (activity.date
                          ? formatDateRange(activity.date, null)
                          : 'Ongoing')}
                  </span>
                ) : (
                  <span className="text-[13px] font-bold leading-snug text-gray-900">{formatDateRange(activity.date, activity.date_end)}</span>
                )}
              </div>
              {(activity.start_time || activity.time) && (
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="flex-shrink-0 text-gray-500" />
                  <span className="text-[13px] font-bold text-gray-900">
                    {activity.start_time
                      ? (activity.end_time
                          ? `${formatTime(activity.start_time)} – ${formatTime(activity.end_time)}`
                          : formatTime(activity.start_time))
                      : activity.time}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Happening At */}
          {address && (
            <div className="flex-1 min-w-0 flex flex-col">
              <p className={LABEL_CLS}>Happening at</p>
              <div className="flex items-start gap-1.5 pt-0.5">
                <MapPin size={13} className="flex-shrink-0 mt-[1px] text-gray-500" />
                <span className="text-[13px] font-bold text-gray-900 leading-snug">{address}</span>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Activity update chips */}
      {activity.activity_update_summaries?.length > 0 && (
        <div className="px-4 pt-2 pb-1 border-t border-gray-100">
          {activity.activity_update_summaries.map((upd) => (
            <button
              key={upd.id}
              onClick={(e) => {
                e.stopPropagation();
                track('activity_update_click', { ...cardContext, update_id: upd.id, update_date: upd.published_at || null });
                navigate(`/activities/${activity.slug || activity.id}#activity-updates`);
              }}
              className="flex items-center gap-1.5 py-0.5 text-xs text-green-600 hover:text-green-700 w-full text-left"
              data-testid={`update-chip-${upd.id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
              <span>Update on {formatUpdateChipDate(upd.published_at)}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Sticky wrapper: direct child of card div (tall containing block) ── */}
      <div className={headingOffTop ? 'sticky bottom-0 bg-white z-20 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]' : ''}>
        <ActivityJoinSection
          activityId={activity.id}
          activityName={activity.name}
          activitySlug={activity.slug}
          shareCardContext={cardContext}
          isPast={isPast}
          interested={isInterested}
          interestLoading={false}
          onJoin={() => {
            if (isLoggedIn) {
              track('activity_interested', { ...cardContext, action: isInterested ? 'uninterested' : 'interested' });
            }
            return onInterest(activity.id, activity.changemaker_id, cardContext);
          }}
          registrationLink={activity.registration_link}
          registrationClosed={activity.registration_closed || (activity.is_participants_fixed && activity.slots_left === 0)}
          registrationFull={!!(activity.is_participants_fixed && activity.slots_left === 0)}
          registrationType={activity.registration_type}
          registrationFields={activity.registration_form_fields}
          registrationRequirement={activity.registration_requirement}
          registrationEndDate={activity.registration_end_date}
          alreadyRegistered={alreadyRegistered}
          embedUrl={getFormEmbedUrl(activity)}
          onRegistered={onRegistered}
          onRegisterButtonClick={() => trackRegistrationClick(activity.id, 'listing_button').catch(() => {})}
          onRegisterClick={() => {
            trackRegistrationClick(activity.id, 'listing_link').catch(() => {});
            track('ext_reg_link_click', { activity_id: activity.id, source_page: 'activities', activity_name: activity.name });
          }}
          sourceProps={sourceProps}
          activityJourney={activity.activity_journey === true}
          showTutorial={showCardTutorial}
          onTutorialDismiss={onTutorialDismiss}
        />
      </div>

      {/* FeedbackForm — triggered by server-side attended status */}
      <div className={`overflow-hidden transition-all duration-300 ${attendedLocal ? 'max-h-[600px]' : 'max-h-0'}`}>
        <FeedbackForm activityId={activity.id} feedbackStatus={feedbackStatus} />
      </div>

      {lightboxOpen && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          currentIdx={lightboxIdx}
          onClose={closeLightbox}
          onSetIdx={setLightboxIdx}
        />
      )}
    </div>
  );
};

export default ActivityCard;
